/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { format, isAfter, isBefore, addDays, differenceInDays } from "date-fns";
import Layout from "../../layout/Main/Layout";
import CustomTable from "../../components/Custom/Table/CustomTable";
import { get, put, post, patch } from "../../config/axios";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress, 
  DialogTitle,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  TextField
} from "@mui/material";
import Searchbar from "../../components/Custom/SearchBar/Searchbar";
import { useDebouncedValue } from "../../helper/debounce";
import { toastMessage } from "../../utils/toastMessage";
import { DietMealPlanReviewTableColumns } from "../../constants/DietMealPlanReview";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { EditableMealPlanTable } from '../DietMealPlan/EditableMealPlanTable';

const DietMealPlanReview = () => {
  const [mealPlans, setMealPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedValue(searchValue, 500);

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [dueDateFilter, setDueDateFilter] = useState("all");
  const [dietTypeFilter, setDietTypeFilter] = useState("all");
  const [weekFilter, setWeekFilter] = useState("all");

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedMealPlan, setSelectedMealPlan] = useState(null);
  const [activationLoading, setActivationLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editableMealPlan, setEditableMealPlan] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Filter options - only for pending review plans
  const statusOptions = [
    { value: "all", label: "All Pending Plans" },
    { value: "pending_review", label: "Pending Review" },
    { value: "ready_to_activate", label: "Ready to Activate" },
    { value: "needs_editing", label: "Needs Editing" }
  ];

  const dueDateOptions = [
    { value: "all", label: "All Due Dates" },
    { value: "today", label: "Due Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" }
  ];

  const dietTypeOptions = [
    { value: "all", label: "All Diet Types" },
    { value: "traditional", label: "Traditional" },
    { value: "low_fodmap", label: "Low FODMAP" }
  ];

  const weekOptions = [
    { value: "all", label: "All Weeks" },
    { value: "1-4", label: "Week 1-4" },
    { value: "5-8", label: "Week 5-8" },
    { value: "9-12", label: "Week 9-12" }
  ];

  // Calculate meal plan status for pending review
  const calculateMealPlanStatus = (mealPlan) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate due date (assuming 7 days from creation for weekly plans)
    const createdDate = new Date(mealPlan.createdAt);
    const dueDate = addDays(createdDate, 7);
    dueDate.setHours(0, 0, 0, 0);
    
    const daysUntilDue = differenceInDays(dueDate, today);
    const isOverdue = isBefore(dueDate, today);
    const isDueToday = daysUntilDue === 0;
    const isDueSoon = daysUntilDue >= 1 && daysUntilDue <= 3;
    
    // Show plans that are NOT overdue (but can be active or inactive)
    if (isOverdue) {
      return null; // This plan should not appear in review queue
    }
    
    let status = "pending_review";
    let statusColor = "warning";
    let statusLabel = "Pending Review";
    
    if (isDueToday) {
      status = "due_today";
      statusColor = "warning";
      statusLabel = "Due Today";
    } else if (isDueSoon) {
      status = "due_soon";
      statusColor = "info";
      statusLabel = "Due Soon";
    } else if (daysUntilDue > 3) {
      status = "ready_to_activate";
      statusColor = "success";
      statusLabel = "Ready to Activate";
    }

    return {
      status,
      statusColor,
      statusLabel,
      dueDate: format(dueDate, "dd/MM/yyyy"),
      daysUntilDue,
      isDueToday,
      isDueSoon,
      canActivate: !mealPlan.active, // Can activate if not already active
      isActive: mealPlan.active
    };
  };

  // Fetch meal plans for review - only pending ones
  const fetchMealPlansForReview = async (searchValue) => {
    try {
      setLoading(true);
      
      const response = await get(
        `/api/dashboard/diet/mealPlanGetAdmin?page=${page}&limit=${perPage}&search=${searchValue || ""}`
      );
      
      console.log("Raw API response:", response); // Debug log
      
      // Check if response is an error object
      if (response && response.errorType) {
        console.error("API Error:", response);
        toastMessage(response.message || "Failed to fetch meal plans", "error");
        setMealPlans([]);
        setLoading(false);
        return;
      }
      
      if (response?.data && Array.isArray(response.data)) {
        console.log("Total meal plans received:", response.data.length); // Debug log
        
        // Filter to only show plans that are NOT overdue (but can be active)
        const pendingPlans = response.data.filter(plan => {
          const statusInfo = calculateMealPlanStatus(plan);
          const shouldInclude = statusInfo !== null;
          console.log(`Plan ${plan._id}: active=${plan.active}, shouldInclude=${shouldInclude}, status=${statusInfo?.statusLabel}`); // Debug log
          return shouldInclude;
        });

        console.log("Plans after filtering:", pendingPlans.length); // Debug log

        const enrichedPlans = pendingPlans.map((plan, index) => {
          const statusInfo = calculateMealPlanStatus(plan);
          console.log(`Processing plan ${plan._id}:`, {
            weeklyPlan: plan.weeklyPlan,
            shoppingList: plan.shoppingList,
            dietInfo: plan.dietInfo
          });
          return {
            ...plan,
            SNo: (page - 1) * perPage + index + 1,
            action: { edit: true, view: true, activate: !plan.active },
            patientName: plan.patient?.fullname || "Unknown",
            dietType: plan.dietType || "Unknown",
            phase: plan.dietInfo?.phase || plan.dietInfo?.lowFodmap?.currentPhase || "N/A",
            currentWeek: plan.dietInfo?.currentWeek || plan.dietInfo?.lowFodmap?.currentWeek || "N/A",
            dueDate: statusInfo.dueDate,
            approvalWindow: statusInfo.statusLabel,
            statusInfo: statusInfo,
            createdAt: format(new Date(plan.createdAt), "dd MMM yyyy"),
            updatedAt: format(new Date(plan.updatedAt), "dd MMM yyyy"),
          };
        });

        setMealPlans(enrichedPlans);
        // Calculate pagination based on filtered results from current page
        // Since filtering happens client-side after API pagination, we base pagination on what we actually have
        const filteredCount = enrichedPlans.length;
        // If we have fewer items than perPage, we only have 1 page
        // Otherwise, we use the API's totalPages but ensure it's at least 1
        const calculatedPageCount = filteredCount <= perPage ? 1 : Math.max(1, response.totalPages || 1);
        setPageCount(calculatedPageCount);
        setTotalData(filteredCount);
        setPerPage(response.perPage || 10);
        // If current page exceeds available data, reset to page 1
        const safePage = filteredCount === 0 ? 1 : Math.min(page, calculatedPageCount);
        if (safePage !== page) {
          setPage(1);
        }
        setCurrentPage(safePage);
      } else {
        console.log("No data received or invalid response structure:", response);
        setMealPlans([]);
        setPageCount(1);
        setTotalData(0);
        toastMessage("No meal plans found or invalid response", "warning");
      }
    } catch (error) {
      console.error("Failed to fetch meal plans for review:", error);
      toastMessage("Failed to fetch meal plans", "error");
    } finally {
      setLoading(false);
    }
  };

  // Get filtered meal plans
  const getFilteredMealPlans = () => {
    let filtered = [...mealPlans]; // Create a copy to avoid mutating state

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(plan => {
        if (statusFilter === "active") {
          return plan.active === true;
        }
        return plan.statusInfo.status === statusFilter;
      });
    }

    // Filter by due date
    if (dueDateFilter !== "all") {
      filtered = filtered.filter(plan => {
        switch (dueDateFilter) {
          case "today":
            return plan.statusInfo.isDueToday;
          case "week":
            return plan.statusInfo.daysUntilDue >= 0 && plan.statusInfo.daysUntilDue <= 7;
          case "month":
            return plan.statusInfo.daysUntilDue >= 0 && plan.statusInfo.daysUntilDue <= 30;
          default:
            return true;
        }
      });
    }

    // Filter by diet type
    if (dietTypeFilter !== "all") {
      filtered = filtered.filter(plan => plan.dietType === dietTypeFilter);
    }

    // Filter by week range
    if (weekFilter !== "all") {
      filtered = filtered.filter(plan => {
        const week = plan.currentWeek;
        if (week === "N/A") return false;
        
        switch (weekFilter) {
          case "1-4":
            return week >= 1 && week <= 4;
          case "5-8":
            return week >= 5 && week <= 8;
          case "9-12":
            return week >= 9 && week <= 12;
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  // Handle filters
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case "status":
        setStatusFilter(value);
        break;
      case "dueDate":
        setDueDateFilter(value);
        break;
      case "dietType":
        setDietTypeFilter(value);
        break;
      case "week":
        setWeekFilter(value);
        break;
    }
    setPage(1);
  };

  // Reset all filters
  const resetFilters = () => {
    setStatusFilter("all");
    setDueDateFilter("all");
    setDietTypeFilter("all");
    setWeekFilter("all");
    setPage(1);
  };

  // Handle meal plan activation
  const handleActivateMealPlan = async (mealPlan) => {
    try {
      setActivationLoading(true);
      
      const response = await put(`/api/app/diet/toggleMealPlanActive/${mealPlan._id}/active`, {
        active: true
      });

      if (response?.data) {
        toastMessage("Meal plan activated successfully!", "success");
        // Refresh the data
        await fetchMealPlansForReview(searchValue);
      }
    } catch (error) {
      console.error("Failed to activate meal plan:", error);
      toastMessage("Failed to activate meal plan", "error");
    } finally {
      setActivationLoading(false);
    }
  };

  // Handle view meal plan details
  const handleViewMealPlan = (mealPlan) => {
    console.log("Viewing meal plan:", mealPlan);
    console.log("Weekly plan data:", mealPlan.weeklyPlan);
    console.log("Shopping list data:", mealPlan.shoppingList);
    setSelectedMealPlan(mealPlan);
    setViewModalOpen(true);
  };

  // Handle edit meal plan
  const handleEditMealPlan = (mealPlan) => {
    setSelectedMealPlan(mealPlan);
    setEditableMealPlan({ ...mealPlan }); // Create editable copy
    setEditModalOpen(true);
    setIsEditing(true);
    setHasChanges(false);
  };

  // Handle close edit modal
  const handleCloseEditModal = () => {
    if (hasChanges) {
      if (window.confirm("You have unsaved changes. Are you sure you want to close?")) {
        setEditModalOpen(false);
        setIsEditing(false);
        setSelectedMealPlan(null);
        setEditableMealPlan(null);
        setEditLoading(false);
        setHasChanges(false);
      }
    } else {
      setEditModalOpen(false);
      setIsEditing(false);
      setSelectedMealPlan(null);
      setEditableMealPlan(null);
      setEditLoading(false);
      setHasChanges(false);
    }
  };

  // Refresh meal plan data after editing
  const refreshMealPlan = async () => {
    try {
      setEditLoading(true);
      const response = await get(`/api/app/diet/mealPlanGetAdmin?page=${page}&limit=${perPage}&search=${searchValue || ""}`);
      if (response?.data && Array.isArray(response.data)) {
        const updatedMealPlan = response.data.find(plan => plan._id === selectedMealPlan._id);
        if (updatedMealPlan) {
          setSelectedMealPlan(updatedMealPlan);
          // Also update the meal plans list
          const enrichedPlans = response.data.map((plan, index) => {
            const statusInfo = calculateMealPlanStatus(plan);
            return {
              ...plan,
              SNo: (page - 1) * perPage + index + 1,
              action: { edit: true, view: true, activate: !plan.active },
              patientName: plan.patient?.fullname || "Unknown",
              dietType: plan.dietType || "Unknown",
              phase: plan.dietInfo?.phase || plan.dietInfo?.lowFodmap?.currentPhase || "N/A",
              currentWeek: plan.dietInfo?.currentWeek || plan.dietInfo?.lowFodmap?.currentWeek || "N/A",
              dueDate: statusInfo.dueDate,
              approvalWindow: statusInfo.statusLabel,
              statusInfo: statusInfo,
              createdAt: format(new Date(plan.createdAt), "dd MMM yyyy"),
              updatedAt: format(new Date(plan.updatedAt), "dd MMM yyyy"),
            };
          });
          setMealPlans(enrichedPlans);
          toastMessage("Meal plan updated successfully!", "success");
        }
      }
    } catch (error) {
      console.error("Error refreshing meal plan:", error);
      toastMessage("Error refreshing meal plan data", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Handle changes to editable fields
  const handleFieldChange = (field, value) => {
    setEditableMealPlan(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Handle diet info changes
  const handleDietInfoChange = (field, value) => {
    setEditableMealPlan(prev => ({
      ...prev,
      dietInfo: {
        ...prev.dietInfo,
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  // Handle low FODMAP changes
  const handleLowFodmapChange = (field, value) => {
    setEditableMealPlan(prev => ({
      ...prev,
      dietInfo: {
        ...prev.dietInfo,
        lowFodmap: {
          ...prev.dietInfo.lowFodmap,
          [field]: value
        }
      }
    }));
    setHasChanges(true);
  };

  // Handle shopping list changes
  const handleShoppingListChange = (categoryIdx, itemIdx, field, value) => {
    setEditableMealPlan(prev => {
      const newShoppingList = [...prev.shoppingList];
      if (field === 'category') {
        newShoppingList[categoryIdx].category = value;
      } else if (field === 'item') {
        newShoppingList[categoryIdx].items[itemIdx].name = value;
      } else if (field === 'quantity') {
        newShoppingList[categoryIdx].items[itemIdx].quantity = value;
      } else if (field === 'unit') {
        newShoppingList[categoryIdx].items[itemIdx].unit = value;
      }
      return {
        ...prev,
        shoppingList: newShoppingList
      };
    });
    setHasChanges(true);
  };

  // Add new shopping list category
  const handleAddShoppingCategory = () => {
    setEditableMealPlan(prev => ({
      ...prev,
      shoppingList: [
        ...prev.shoppingList,
        {
          category: "New Category",
          items: []
        }
      ]
    }));
    setHasChanges(true);
  };

  // Add new shopping list item
  const handleAddShoppingItem = (categoryIdx) => {
    setEditableMealPlan(prev => {
      const newShoppingList = [...prev.shoppingList];
      newShoppingList[categoryIdx].items.push({
        name: "",
        quantity: 1,
        unit: "piece"
      });
      return {
        ...prev,
        shoppingList: newShoppingList
      };
    });
    setHasChanges(true);
  };

  // Remove shopping list item
  const handleRemoveShoppingItem = (categoryIdx, itemIdx) => {
    setEditableMealPlan(prev => {
      const newShoppingList = [...prev.shoppingList];
      newShoppingList[categoryIdx].items.splice(itemIdx, 1);
      return {
        ...prev,
        shoppingList: newShoppingList
      };
    });
    setHasChanges(true);
  };

  // Remove shopping list category
  const handleRemoveShoppingCategory = (categoryIdx) => {
    setEditableMealPlan(prev => {
      const newShoppingList = [...prev.shoppingList];
      newShoppingList.splice(categoryIdx, 1);
      return {
        ...prev,
        shoppingList: newShoppingList
      };
    });
    setHasChanges(true);
  };

  // Save all changes
  const handleSaveAllChanges = async () => {
    try {
      setEditLoading(true);
      
      // Update the selected meal plan with all changes
      setSelectedMealPlan(editableMealPlan);
      
      // Here you would typically make API calls to save all the changes
      // For now, we'll just update the local state and show success
      toastMessage("All changes saved successfully!", "success");
      setHasChanges(false);
      
      // Refresh the meal plan data
      await refreshMealPlan();
    } catch (error) {
      console.error("Error saving changes:", error);
      toastMessage("Error saving changes", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    const filteredCount = getFilteredMealPlans().length;
    const maxPage = Math.max(1, Math.ceil(filteredCount / perPage));
    // Prevent navigating to pages beyond available data
    if (newPage >= 1 && newPage <= maxPage) {
      setPage(newPage);
    } else if (newPage > maxPage) {
      // If trying to go beyond available pages, go to last page
      setPage(maxPage);
    }
  };

  // Handle search
  const handleSearch = (searchText) => {
    setSearchValue(searchText.trim());
    setPage(1);
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "due_today":
        return <WarningIcon color="warning" />;
      case "due_soon":
        return <WarningIcon color="info" />;
      case "ready_to_activate":
        return <CheckCircleIcon color="success" />;
      case "pending_review":
        return <PlayArrowIcon color="action" />;
      default:
        return <PlayArrowIcon color="action" />;
    }
  };

  // Effects
  useEffect(() => {
    fetchMealPlansForReview(debouncedSearch);
  }, [debouncedSearch, page]);

  const filteredMealPlans = getFilteredMealPlans();

  return (
    <Layout>
      <div style={{ padding: "1rem" }}>
        <Typography variant="h5" gutterBottom>
          Meal Plan Review Queue
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Review and activate pending meal plans that are ready for patients
        </Typography>

        {/* Search and Filters */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2
        }}>
          <div style={{ width: "40%" }}>
            <Searchbar
              search={handleSearch}
              placeholder={"Search by Patient Name"}
              debounceTime={1000}
            />
          </div>

          {/* Filters */}
          <Box sx={{ 
            display: "flex", 
            gap: 2, 
            flexWrap: "wrap",
            alignItems: "center"
          }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Due Date</InputLabel>
              <Select
                value={dueDateFilter}
                label="Due Date"
                onChange={(e) => handleFilterChange("dueDate", e.target.value)}
              >
                {dueDateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Diet Type</InputLabel>
              <Select
                value={dietTypeFilter}
                label="Diet Type"
                onChange={(e) => handleFilterChange("dietType", e.target.value)}
              >
                {dietTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Week Range</InputLabel>
              <Select
                value={weekFilter}
                label="Week Range"
                onChange={(e) => handleFilterChange("week", e.target.value)}
              >
                {weekOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              onClick={resetFilters}
              disabled={statusFilter === "all" && dueDateFilter === "all" && dietTypeFilter === "all" && weekFilter === "all"}
            >
              Reset Filters
            </Button>
          </Box>
        </Box>

        {/* Filter Summary */}
        {(statusFilter !== "all" || dueDateFilter !== "all" || dietTypeFilter !== "all" || weekFilter !== "all") && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Active Filters:
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {statusFilter !== "all" && (
                <Chip
                  label={statusOptions.find(opt => opt.value === statusFilter)?.label}
                  color="primary"
                  size="small"
                  onDelete={() => handleFilterChange("status", "all")}
                />
              )}
              {dueDateFilter !== "all" && (
                <Chip
                  label={dueDateOptions.find(opt => opt.value === dueDateFilter)?.label}
                  color="secondary"
                  size="small"
                  onDelete={() => handleFilterChange("dueDate", "all")}
                />
              )}
              {dietTypeFilter !== "all" && (
                <Chip
                  label={dietTypeOptions.find(opt => opt.value === dietTypeFilter)?.label}
                  color="success"
                  size="small"
                  onDelete={() => handleFilterChange("dietType", "all")}
                />
              )}
              {weekFilter !== "all" && (
                <Chip
                  label={weekOptions.find(opt => opt.value === weekFilter)?.label}
                  color="info"
                  size="small"
                  onDelete={() => handleFilterChange("week", "all")}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Statistics Summary - Hidden */}
        {/* <Box sx={{ 
          display: "flex", 
          gap: 2, 
          mb: 3,
          flexWrap: "wrap",
          alignItems: "center"
        }}>
          <Typography variant="body2" color="text.secondary">
            Meal Plan Summary:
          </Typography>
          <Chip
            label={`Due Today: ${mealPlans.filter(plan => plan.statusInfo.isDueToday).length}`}
            color="warning"
            size="small"
            variant="outlined"
            icon={<WarningIcon />}
          />
          <Chip
            label={`Due Soon: ${mealPlans.filter(plan => plan.statusInfo.isDueSoon).length}`}
            color="info"
            size="small"
            variant="outlined"
            icon={<WarningIcon />}
          />
          <Chip
            label={`Ready to Activate: ${mealPlans.filter(plan => plan.statusInfo.status === "ready_to_activate").length}`}
            color="success"
            size="small"
            variant="outlined"
            icon={<CheckCircleIcon />}
          />
          <Chip
            label={`Pending Review: ${mealPlans.filter(plan => plan.statusInfo.status === "pending_review").length}`}
            color="default"
            size="small"
            variant="outlined"
            icon={<PlayArrowIcon />}
          />
          <Chip
            label={`Active: ${mealPlans.filter(plan => plan.active).length}`}
            color="success"
            size="small"
            variant="outlined"
            icon={<CheckCircleIcon />}
          />
          <Chip
            label={`Total: ${mealPlans.length}`}
            color="default"
            size="small"
            variant="outlined"
          />
        </Box> */}

        {/* Debug Information - Hidden */}
        {/* {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Debug Info (Development Only):
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
              Loading: {loading.toString()}<br/>
              Total Plans: {mealPlans.length}<br/>
              Page: {page}<br/>
              Search Value: "{searchValue}"<br/>
              API Response: {mealPlans.length > 0 ? 'Data received' : 'No data or filtered out'}
            </Typography>
          </Box>
        )} */}

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Review Queue:</strong> This page shows meal plans that are NOT overdue. 
          You can review, edit, or activate these plans. Active plans can be viewed and edited, 
          while inactive plans can be activated.
        </Alert>

        {/* Main Table */}
        <CustomTable
          data={filteredMealPlans}
          columns={DietMealPlanReviewTableColumns}
          handleEdit={handleEditMealPlan}
          handleDelete={() => {}} // Disable delete in review mode
          handleStatus={() => {}} // Disable status change in review mode
          handleDisplay={handleViewMealPlan}
          handleActive={(row, active) => handleActivateMealPlan(row)}
          handlePageChange={handlePageChange}
          pageNumber={page}
          pageCount={Math.max(1, Math.ceil(filteredMealPlans.length / perPage))}
          totalData={filteredMealPlans.length}
          perPage={perPage}
          currentPage={currentPage}
          loading={loading}
          customRender={{
            approvalWindow: (value, row) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {getStatusIcon(row.statusInfo.status)}
                <Chip
                  label={value}
                  color={row.statusInfo.statusColor}
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    // Green (success) -> approve/activate
                    if (row.statusInfo.statusColor === 'success' && !row.active) {
                      try {
                        const res = await put(`/api/dashboard/diet/toggleMealPlanActive/${row._id}`, { active: true });
                        if (res?.success) {
                          toastMessage('Meal plan activated', 'success');
                          await fetchMealPlansForReview("");
                        } else {
                          toastMessage(res?.message || 'Failed to activate', 'error');
                        }
                      } catch (e) {
                        toastMessage('Failed to activate plan', 'error');
                      }
                    }
                  }}
                />
              </Box>
            ),
            "currentWeek": (value) => (
              <Chip
                label={`Week ${value}`}
                color="info"
                size="small"
                variant="outlined"
              />
            ),
            "action": (value, row) => (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    onClick={() => handleViewMealPlan(row)}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Meal Plan">
                  <IconButton
                    size="small"
                    onClick={() => handleEditMealPlan(row)}
                    color="secondary"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Activate Meal Plan">
                  <IconButton
                    size="small"
                    onClick={() => handleActivateMealPlan(row)}
                    color="success"
                    disabled={activationLoading}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          }}
        />

        {/* View Meal Plan Modal */}
        <Dialog
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Meal Plan Review: {selectedMealPlan?.patientName}
          </DialogTitle>
          <DialogContent dividers>
            {selectedMealPlan && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Plan Information
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Patient:</strong> {selectedMealPlan.patientName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Diet Type:</strong> {selectedMealPlan.dietType}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phase:</strong> {selectedMealPlan.phase}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Current Week:</strong> {selectedMealPlan.currentWeek}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Due Date:</strong> {selectedMealPlan.dueDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {selectedMealPlan.approvalWindow}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Created:</strong> {selectedMealPlan.createdAt}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Active:</strong> {selectedMealPlan.active ? "Yes" : "No"}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Weekly Plan Overview
                </Typography>
                {selectedMealPlan.weeklyPlan && selectedMealPlan.weeklyPlan.length > 0 ? (
                  selectedMealPlan.weeklyPlan.map((dayPlan, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {dayPlan.day || 'Unknown Day'} - {dayPlan.date ? format(new Date(dayPlan.date), "dd MMM yyyy") : 'No Date'}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                      {dayPlan.meals?.map((meal, mealIdx) => (
                        <Box key={mealIdx}>
                          <Typography variant="body2" color="primary" gutterBottom>
                            {meal.type}
                          </Typography>
                          {meal.foods?.map((food, foodIdx) => (
                            <Typography key={foodIdx} variant="body2" sx={{ ml: 2 }}>
                              • {food.name} ({food.quantity?.value} {food.quantity?.unit})
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                    No weekly plan data available
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Shopping List
                </Typography>
                {selectedMealPlan.shoppingList && selectedMealPlan.shoppingList.length > 0 ? (
                  selectedMealPlan.shoppingList.map((category, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        {category.category}
                      </Typography>
                      {category.items?.map((item, itemIdx) => (
                        <Typography key={itemIdx} variant="body2" sx={{ ml: 2 }}>
                          • {item.name} - {item.quantity} {item.unit}
                        </Typography>
                      ))}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                    No shopping list available
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModalOpen(false)}>Close</Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setViewModalOpen(false);
                handleEditMealPlan(selectedMealPlan);
              }}
              startIcon={<EditIcon />}
            >
              Edit Plan
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                handleActivateMealPlan(selectedMealPlan);
                setViewModalOpen(false);
              }}
              disabled={activationLoading}
              startIcon={<PlayArrowIcon />}
            >
              Activate Plan
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Meal Plan Modal */}
        <Dialog
          open={editModalOpen}
          onClose={handleCloseEditModal}
          maxWidth="xl"
          fullWidth
        >
          <DialogTitle>
            Edit Meal Plan: {selectedMealPlan?.patientName}
          </DialogTitle>
          <DialogContent dividers>
            {editLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Updating meal plan...
                </Typography>
              </Box>
            )}
            {editableMealPlan && !editLoading && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Plan Information
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mb: 3 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Patient:</strong>
                    </Typography>
                    <TextField
                      size="small"
                      value={editableMealPlan.patientName || ""}
                      onChange={(e) => handleFieldChange('patientName', e.target.value)}
                      fullWidth
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Diet Type:</strong>
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={editableMealPlan.dietType || "traditional"}
                        onChange={(e) => handleFieldChange('dietType', e.target.value)}
                      >
                        <MenuItem value="traditional">Traditional</MenuItem>
                        <MenuItem value="low_fodmap">Low FODMAP</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Phase:</strong>
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={editableMealPlan.dietInfo?.phase || editableMealPlan.dietInfo?.lowFodmap?.currentPhase || "elimination"}
                        onChange={(e) => handleDietInfoChange('phase', e.target.value)}
                      >
                        <MenuItem value="elimination">Elimination</MenuItem>
                        <MenuItem value="introduction">Introduction</MenuItem>
                        <MenuItem value="maintenance">Maintenance</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Current Week:</strong>
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={editableMealPlan.dietInfo?.currentWeek || editableMealPlan.dietInfo?.lowFodmap?.currentWeek || 1}
                      onChange={(e) => handleDietInfoChange('currentWeek', parseInt(e.target.value) || 1)}
                      fullWidth
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Status:</strong>
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={editableMealPlan.dietInfo?.status || "Active"}
                        onChange={(e) => handleDietInfoChange('status', e.target.value)}
                      >
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Active:</strong>
                    </Typography>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={editableMealPlan.active ? "Yes" : "No"}
                        onChange={(e) => handleFieldChange('active', e.target.value === "Yes")}
                      >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Weekly Plan - Edit Mode
                </Typography>
                {editableMealPlan.weeklyPlan && editableMealPlan.weeklyPlan.length > 0 ? (
                  editableMealPlan.weeklyPlan.map((dayPlan, idx) => (
                    <Box key={idx} sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: "primary.main", fontWeight: "bold" }}>
                        {dayPlan.day?.charAt(0).toUpperCase() + dayPlan.day?.slice(1) || 'Unknown Day'} | {dayPlan.date ? format(new Date(dayPlan.date), "dd MMM yyyy") : 'No Date'}
                      </Typography>
                      <EditableMealPlanTable
                        dayPlan={dayPlan}
                        planId={editableMealPlan._id}
                        onUpdate={async (updatedDayPlan) => {
                          console.log('Received updated day plan:', updatedDayPlan);
                          const updatedWeekly = [...editableMealPlan.weeklyPlan];
                          updatedWeekly[idx] = updatedDayPlan;
                          console.log('Updated weekly plan:', updatedWeekly);
                          setEditableMealPlan({ ...editableMealPlan, weeklyPlan: updatedWeekly });
                          setHasChanges(true);
                        }}
                      />
                    </Box>
                  ))
                ) : (
                  <Alert severity="warning" sx={{ p: 2 }}>
                    <Typography variant="body2">
                      No weekly plan data available for editing. This meal plan may not have been properly generated.
                    </Typography>
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Shopping List
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddShoppingCategory}
                  >
                    Add Category
                  </Button>
                </Box>
                {editableMealPlan.shoppingList && editableMealPlan.shoppingList.length > 0 ? (
                  editableMealPlan.shoppingList.map((category, categoryIdx) => (
                    <Paper key={categoryIdx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <TextField
                          size="small"
                          value={category.category}
                          onChange={(e) => handleShoppingListChange(categoryIdx, null, 'category', e.target.value)}
                          sx={{ minWidth: 200 }}
                        />
                        <Box>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddShoppingItem(categoryIdx)}
                            sx={{ mr: 1 }}
                          >
                            Add Item
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleRemoveShoppingCategory(categoryIdx)}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Box>
                      {category.items?.map((item, itemIdx) => (
                        <Box key={itemIdx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            placeholder="Item name"
                            value={item.name}
                            onChange={(e) => handleShoppingListChange(categoryIdx, itemIdx, 'item', e.target.value)}
                            sx={{ flex: 2 }}
                          />
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => handleShoppingListChange(categoryIdx, itemIdx, 'quantity', parseInt(e.target.value) || 1)}
                            sx={{ width: 80 }}
                          />
                          <TextField
                            size="small"
                            placeholder="Unit"
                            value={item.unit}
                            onChange={(e) => handleShoppingListChange(categoryIdx, itemIdx, 'unit', e.target.value)}
                            sx={{ width: 100 }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveShoppingItem(categoryIdx, itemIdx)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Paper>
                  ))
                ) : (
                  <Alert severity="info" sx={{ p: 2 }}>
                    <Typography variant="body2">
                      No shopping list available. Click "Add Category" to create one.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditModal}>
              {hasChanges ? "Cancel" : "Close"}
            </Button>
            {hasChanges && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAllChanges}
                disabled={editLoading}
                startIcon={editLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              >
                {editLoading ? "Saving..." : "Save All Changes"}
              </Button>
            )}
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                handleActivateMealPlan(editableMealPlan);
                handleCloseEditModal();
              }}
              disabled={activationLoading || editLoading}
              startIcon={<PlayArrowIcon />}
            >
              Activate Plan
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DietMealPlanReview;
